import { useState, useMemo } from "react";
import BaseModal from "./BaseModal";
import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import CheckBox from "@components/ui/selection/CheckBox";
import { useDataStore } from "@store/user_org";

type Reminder = {
  id: string;
  type: string;
  scheduled_at: Date;
  enabled: boolean;
};

type Props = {
  onClose: () => void;
  onConfirm: (remindersEnabled: boolean, disabledReminders: string[]) => void;
  receptionDate: Date | null;
  clientEmail: string;
  loading?: boolean;
};

function computePreviewSchedule(
  sendTime: Date,
  receptionDt: Date,
  config: {
    r1_delay_hours: number;
    r2_delay_hours: number;
    r3_before_reception_hours: number;
    cancel_before_reception_hours: number;
    min_gap_hours: number;
  },
): Reminder[] {
  const hoursUntilReception =
    (receptionDt.getTime() - sendTime.getTime()) / (1000 * 60 * 60);

  const candidates: Reminder[] = [];

  if (hoursUntilReception > 48) {
    candidates.push({
      id: "r1",
      type: "reminder_24h",
      scheduled_at: new Date(sendTime.getTime() + config.r1_delay_hours * 3600000),
      enabled: true,
    });
    candidates.push({
      id: "r2",
      type: "reminder_48h",
      scheduled_at: new Date(sendTime.getTime() + config.r2_delay_hours * 3600000),
      enabled: true,
    });
  }

  const r3Time = new Date(receptionDt.getTime() - config.r3_before_reception_hours * 3600000);
  if (r3Time.getTime() > sendTime.getTime() + 3600000) {
    candidates.push({
      id: "r3",
      type: "reminder_urgent",
      scheduled_at: r3Time,
      enabled: true,
    });
  }

  candidates.sort((a, b) => a.scheduled_at.getTime() - b.scheduled_at.getTime());

  const filtered: Reminder[] = [];
  for (const c of candidates) {
    if (filtered.length > 0) {
      const lastActive = [...filtered].reverse().find((f) => f.enabled);
      if (lastActive) {
        const gap =
          (c.scheduled_at.getTime() - lastActive.scheduled_at.getTime()) / (1000 * 60 * 60);
        if (gap < config.min_gap_hours && c.id !== "r3") {
          filtered.push({ ...c, enabled: false });
          continue;
        }
      }
    }
    filtered.push(c);
  }

  const cancelTime = new Date(
    receptionDt.getTime() - config.cancel_before_reception_hours * 3600000,
  );
  if (cancelTime.getTime() > sendTime.getTime()) {
    filtered.push({
      id: "cancel",
      type: "auto_cancel",
      scheduled_at: cancelTime,
      enabled: true,
    });
  }

  return filtered;
}

const LABEL_MAP: Record<string, string> = {
  r1: "Rappel 1 (24h)",
  r2: "Rappel 2 (48h)",
  r3: "Rappel urgent",
  cancel: "Annulation automatique",
};

function formatDateTime(d: Date): string {
  return d.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }) + " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export default function DevisSendPreviewModal({
  onClose,
  onConfirm,
  receptionDate,
  clientEmail,
  loading,
}: Props) {
  const orgData = useDataStore((s) => s.orgData);
  const reminderConfig = orgData?.config?.devis_reminders || {};

  const config = {
    r1_delay_hours: reminderConfig.r1_delay_hours ?? 24,
    r2_delay_hours: reminderConfig.r2_delay_hours ?? 48,
    r3_before_reception_hours: reminderConfig.r3_before_reception_hours ?? 48,
    cancel_before_reception_hours: reminderConfig.cancel_before_reception_hours ?? 42,
    min_gap_hours: reminderConfig.min_gap_hours ?? 12,
  };

  const orgEnabled = reminderConfig.enabled !== false;

  const schedule = useMemo(() => {
    if (!receptionDate) return [];
    return computePreviewSchedule(new Date(), receptionDate, config);
  }, [receptionDate, config]);

  const [globalEnabled, setGlobalEnabled] = useState(orgEnabled);
  const [disabledIds, setDisabledIds] = useState<Set<string>>(new Set());

  const toggleReminder = (id: string) => {
    if (id === "cancel") return;
    setDisabledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(globalEnabled, Array.from(disabledIds));
  };

  return (
    <BaseModal title="Envoyer le devis au client" onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        <div className="text-sm text-gray-600">
          Le devis sera envoyé à : <strong>{clientEmail || "—"}</strong>
        </div>

        {receptionDate && (
          <>
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium">Rappels planifiés</span>
                <div className="flex items-center gap-2">
                  <CheckBox
                    checked={globalEnabled}
                    onChange={() => setGlobalEnabled(!globalEnabled)}
                  />
                  <span className="text-h3 cursor-pointer" onClick={() => setGlobalEnabled(!globalEnabled)}>Activer les rappels</span>
                </div>
              </div>

              {globalEnabled && schedule.length > 0 && (
                <div className="flex flex-col gap-2">
                  {schedule.map((r) => {
                    const isCancel = r.id === "cancel";
                    const isSkipped = !r.enabled;
                    const isUserDisabled = disabledIds.has(r.id);
                    const active = r.enabled && !isUserDisabled;

                    return (
                      <div
                        key={r.id}
                        className={`flex items-center gap-2 text-sm rounded px-3 py-2 ${
                          isCancel
                            ? "bg-red-50 text-red-700"
                            : active
                              ? "bg-gray-50"
                              : "bg-gray-50 text-gray-400"
                        }`}
                      >
                        {!isCancel && !isSkipped && (
                          <CheckBox
                            checked={!isUserDisabled}
                            onChange={() => toggleReminder(r.id)}
                          />
                        )}
                        {isCancel && <Icon1 name="warning" size={14} />}
                        {isSkipped && <span className="w-4" />}
                        <span className="flex-1">
                          {LABEL_MAP[r.id] || r.id}
                          {isSkipped && " (écart trop court)"}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(r.scheduled_at)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {globalEnabled && schedule.length === 0 && (
                <p className="text-sm text-gray-400">
                  Aucun rappel planifiable (pas de date de réception)
                </p>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 border-t pt-3">
          <SecondaryButton variant="small" label="Annuler" onClick={onClose} />
          <Button
            variant="small"
            label={loading ? "Envoi..." : "Envoyer"}
            onClick={handleConfirm}
            disabled={loading}
          />
        </div>
      </div>
    </BaseModal>
  );
}
